import React, {useState} from 'react';

import {assert} from 'Asserts';
import {
  AnalyticsDashboardActions,
  createAnalyticsDashboard,
} from 'insights/analytics-dashboards/AnalyticsDashboardActions';
import AnalyticsDescriptionEditor from 'insights/AnalyticsDescriptionEditor';
import AnalyticsProjectSelector, {
  getDefaultProjectId,
} from 'insights/shared/components/AnalyticsProjectSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import useFormInput from 'ui-library/hooks/useFormInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

export type Actions = Pick<AnalyticsDashboardActions, 'createAnalyticsDashboard'>;

export interface CreateAnalyticsDashboardModalProps {
  actions: Actions;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export function CreateAnalyticsDashboardModal(props: CreateAnalyticsDashboardModalProps): JSX.Element {
  const nameFormInput = useFormInput('');
  const [projectId, setProjectId] = useState(getDefaultProjectId());
  const [description, setDescription] = useState<string>('');
  const name = nameFormInput.value;
  const canSubmit = name && name.match(/^\s*$/) == null && projectId != null;

  async function handleSubmit(): Promise<void> {
    assert(projectId != null, 'Unexpected creating dashboard with null project');
    await withLoadingScreen(
      props.actions.createAnalyticsDashboard({
        analyticsQueries: [],
        name,
        description,
        layoutRows: [],
        parameterConfigs: [],
        parameterValues: [],
        projectId,
      }),
      'Creating dashboard...'
    );
    props.onRequestSubmit();
  }

  return (
    <Modal
      buttons={[{disabled: !canSubmit, submit: true, text: 'Create'}]}
      medium={true}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Create dashboard"
    >
      <FormGroup>
        <TextField label="Name" placeholder="New dashboard" autoFocus={true} {...nameFormInput} />
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Project" />
        <AnalyticsProjectSelector onChange={setProjectId} projectId={projectId} />
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Description" />
        <AnalyticsDescriptionEditor description={description} setDescription={setDescription} />
      </FormGroup>
    </Modal>
  );
}

export default function ConnectedCreateAnalyticsDashboardModal(
  props: Omit<CreateAnalyticsDashboardModalProps, 'actions'>
): JSX.Element {
  return <CreateAnalyticsDashboardModal {...props} actions={{createAnalyticsDashboard}} />;
}
